<template>
  <div :data-testid="dataTestid" class="rounded-2xl border border-main bg-surface p-6">
    <p :data-testid="`${dataTestid}-label`" class="txt-label-m-medium text-subtle">{{ label }}</p>

    <template v-if="isDisabled">
      <div class="mt-2 flex items-center gap-2">
        <span :data-testid="`${dataTestid}-value`" class="txt-headline-2xl-bold text-subtle">—</span>
      </div>
      <p v-if="caption" :data-testid="`${dataTestid}-caption`" class="mt-2 txt-body-s-regular text-subtle">{{ caption }}</p>
    </template>

    <div v-else-if="loading" :data-testid="`${dataTestid}-loading`" class="mt-3 animate-pulse space-y-2" aria-hidden="true">
      <div class="h-8 w-24 rounded-sm bg-surface-elevation-2" />
      <div class="h-3 w-32 rounded-sm bg-surface-elevation-2" />
    </div>

    <div v-else-if="state === 'error'" :data-testid="`${dataTestid}-error`" class="mt-2 flex items-center gap-2 text-error">
      <AlertTriangleIcon class="lucide-icon-s shrink-0" aria-hidden="true" />
      <span class="txt-body-s-regular">{{ t('Reviews.product.insights.state.error') }}</span>
    </div>

    <p v-else-if="state !== 'ready'" :data-testid="`${dataTestid}-empty`" class="mt-2 txt-body-m-medium text-subtle">
      {{ t(`Reviews.product.insights.state.${STATE_KEYS[state]}.title`) }}
    </p>

    <template v-else>
      <div class="mt-2 flex items-center gap-2">
        <span :data-testid="`${dataTestid}-value`" class="txt-headline-2xl-bold text-main">{{ value }}</span>
        <ChipBadge v-if="delta" :data-testid="`${dataTestid}-delta`" :color="deltaColor" size="s" rounded>
          <template #prepend>
            <ArrowUpIcon v-if="delta.direction === 'up'" :data-testid="`${dataTestid}-delta-up`" class="lucide-icon-s" aria-hidden="true" />
            <ArrowDownIcon v-else :data-testid="`${dataTestid}-delta-down`" class="lucide-icon-s" aria-hidden="true" />
          </template>
          {{ delta.value }}
        </ChipBadge>
      </div>
      <p v-if="caption" :data-testid="`${dataTestid}-caption`" class="mt-2 txt-body-s-regular text-subtle">
        {{ caption }}
      </p>
    </template>
  </div>
</template>

<script lang="ts">
export interface InsightsKpiTileDelta {
  value: string | number
  direction: 'up' | 'down'
}
</script>

<script setup lang="ts">
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import { AlertTriangleIcon, ArrowUpIcon, ArrowDownIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { STATE_KEYS, type InsightsMetricState } from '@/gql/reviews/mappers/insights/insightsState'

const props = withDefaults(
  defineProps<{
    label: string
    value?: string | number
    delta?: InsightsKpiTileDelta
    caption?: string
    disabled?: boolean
    comingSoon?: boolean
    loading?: boolean
    state?: InsightsMetricState
    dataTestid?: string
  }>(),
  {
    value: undefined,
    delta: undefined,
    caption: undefined,
    disabled: false,
    comingSoon: false,
    loading: false,
    state: 'ready',
    dataTestid: 'insights-kpi-tile',
  },
)

const { t } = useI18n()

const isDisabled = computed(() => props.disabled || props.comingSoon)

const deltaColor = computed(() => (props.delta?.direction === 'up' ? 'success' : 'error'))
</script>
